(() => {
  if (customElements.get('respecto-nv900-features')) return;
  class Nv900Features extends HTMLElement {
    #controller;
    #observer;
    #effectsObserver;
    #water = new Map();
    #resize;
    #frame = 0;
    #active = false;
    #manual = false;
    #lastY = 0;
    #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.#effectsObserver?.disconnect();
      for (const water of this.#water.values()) water.destroy();
      this.#water.clear();
      this.#effectsObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const active = entry.isIntersecting && entry.intersectionRatio >= .35;
          const previous = entry.target.hasAttribute('data-nfx-lit');
          if (entry.target.matches('[data-nfx-effect="shield"]')) {
            if (active === previous) continue;
            entry.target.removeAttribute('data-nfx-lit');
            if (active) {
              for (const motion of entry.target.querySelectorAll('animateMotion')) {
                motion.replaceWith(motion.cloneNode(true));
              }
              // Commit the cleared CSS state before replaying the sequence.
              void entry.target.getBoundingClientRect();
              entry.target.setAttribute('data-nfx-lit', '');
              for (const motion of entry.target.querySelectorAll('animateMotion')) motion.beginElement?.();
            }
            continue;
          }
          entry.target.toggleAttribute('data-nfx-lit', active);
          this.#water.get(entry.target)?.setActive(active);
          for (const motion of entry.target.querySelectorAll('animateMotion')) {
            if (active && !previous) motion.beginElement?.();
            else if (!active && previous) motion.endElement?.();
          }
        }
      }, { threshold: [.35], rootMargin: '-80px 0px -8% 0px' });
      for (const effect of this.querySelectorAll('[data-nfx-effect]')) {
        if (effect.dataset.nfxEffect === 'water') this.#water.set(effect, this.#createWater(effect));
        effect.removeAttribute('data-nfx-lit');
        this.#effectsObserver.observe(effect);
      }
      const scrub = this.querySelector('[data-nfx-scrub]');
      const pin = this.querySelector('[data-nfx-pin]');
      const frame = this.querySelector('[data-nfx-frame]');
      const range = this.querySelector('[data-nfx-range]');
      const heading = this.querySelector('[data-nfx-heading]');
      if (!scrub || !pin || !frame || !range || !heading) return;
      const { signal } = this.#controller;
      const setPosition = (value) => {
        const position = Math.max(0, Math.min(100, value));
        frame.style.setProperty('--nfx-position', `${position.toFixed(3)}%`);
        range.value = `${Math.round(position)}`;
      };
      const measure = () => {
        const header = document.querySelector('header-component, header[role="banner"], #header-component');
        const rect = header?.getBoundingClientRect();
        const top = rect && rect.bottom > 0 ? Math.min(rect.height, rect.bottom) : 0;
        const reserve = window.innerWidth < 750 ? 96 : 32;
        const available = window.innerHeight - top - heading.offsetHeight - reserve;
        this.style.setProperty('--nfx-sticky-top', `${Math.max(0, top)}px`);
        this.style.setProperty('--nfx-frame-max', `${Math.max(220, available)}px`);
        this.toggleAttribute('data-nfx-short', available < (window.innerWidth < 750 ? 320 : 260));
        this.toggleAttribute('data-nfx-reduced', this.#reduced.matches);
        this.style.setProperty('--nfx-pin-height', `${pin.offsetHeight}px`);
      };
      const update = () => {
        this.#frame = 0;
        if (!this.isConnected || !this.#active || this.#manual || this.#reduced.matches) return;
        const rect = scrub.getBoundingClientRect();
        const top = parseFloat(this.style.getPropertyValue('--nfx-sticky-top')) || 0;
        const short = this.hasAttribute('data-nfx-short');
        const distance = short ? window.innerHeight + rect.height : Math.max(1, rect.height - pin.offsetHeight);
        const traveled = short ? window.innerHeight - rect.top : top - rect.top;
        setPosition(traveled / distance * 100);
      };
      const schedule = () => { if (!this.#frame) this.#frame = requestAnimationFrame(update); };
      const onScroll = () => {
        this.#lastY = window.scrollY;
        if (this.#active) schedule();
      };
      range.addEventListener('input', () => { this.#manual = true; setPosition(Number(range.value)); }, { signal });
      const resume = () => { this.#manual = false; };
      window.addEventListener('wheel', resume, { passive: true, signal });
      window.addEventListener('touchstart', resume, { passive: true, signal });
      window.addEventListener('keydown', event => {
        if (event.target !== range && ['PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp', ' '].includes(event.key)) resume();
      }, { signal });
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', () => { measure(); schedule(); }, { passive: true, signal });
      this.#reduced.addEventListener('change', () => { measure(); schedule(); }, { signal });
      this.#observer = new IntersectionObserver(entries => {
        this.#active = entries.some(entry => entry.isIntersecting);
        if (this.#active) { measure(); schedule(); }
      }, { rootMargin: '160px 0px' });
      this.#observer.observe(scrub);
      this.#resize = new ResizeObserver(() => { measure(); schedule(); });
      this.#resize.observe(heading);
      this.#resize.observe(frame);
      measure();
      document.fonts?.ready.then(() => { if (this.isConnected) { measure(); schedule(); } });
    }
    #createWater(canvas) {
      const image = canvas.parentElement?.querySelector('img');
      const context = canvas.getContext('2d');
      if (!image || !context) return { setActive() {}, destroy() {} };
      const { signal } = this.#controller;
      let width = 0, height = 0, scale = 1, frame = 0, lastTime = 0, active = false;
      let drops = [];
      // Normalized outline of the vehicle in both responsive photographs.
      // The round camera detail is an inset, not part of this surface.
      const vehicle = [[.325,.083],[.38,.047],[.53,.005],[.68,0],[.75,.045],[.86,.108],[.925,.197],[.974,.359],[.997,.459],[.999,.843],[.95,.864],[.942,.972],[.906,.999],[.854,.995],[.815,.9],[.405,.89],[.381,.94],[.348,.957],[.25,.949],[.218,.913],[.205,.853],[.174,.805],[.163,.709],[.161,.655],[.171,.591],[.18,.506],[.181,.441],[.19,.411],[.19,.34],[.206,.319],[.24,.28],[.278,.197],[.317,.145]];
      const onVehicle = (pixelX, pixelY) => {
        const x = pixelX / width, y = pixelY / height;
        if (((x - .817) / .17) ** 2 + ((y - .306) / .294) ** 2 < 1) return false;
        let inside = false;
        for (let i = 0, j = vehicle.length - 1; i < vehicle.length; j = i++) {
          const [ax, ay] = vehicle[i], [bx, by] = vehicle[j];
          if ((ay > y) !== (by > y) && x < (bx - ax) * (y - ay) / (by - ay) + ax) inside = !inside;
        }
        return inside;
      };
      const makeDrop = (index, moving) => ({
        x: (.07 + ((index * .371) % .86)) * width,
        y: moving ? (.03 + index * .19) * height : (.06 + ((index * .217) % .87)) * height,
        radius: (moving ? 5.8 + index % 3 * 1.1 : 2.2 + (index % 4) * .6) * scale,
        speed: moving ? (22 + index % 3 * 10) * scale : 0,
        moving, phase: index * 2.43, hidden: 0
      });
      const measure = () => {
        const box = canvas.getBoundingClientRect();
        width = box.width;
        height = box.height;
        scale = Math.min(1, Math.max(.5, width / 713));
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        drops = Array.from({ length: 17 }, (_, index) => makeDrop(index, index < 5)).filter(drop => drop.moving || onVehicle(drop.x, drop.y));
      };
      const drawDrop = drop => {
        const { x, y, radius: r } = drop;
        const rx = r * (1 + Math.sin(drop.phase) * .055);
        const ry = r * (drop.moving ? 1.28 : 1.08);
        const shape = () => {
          context.beginPath();
          context.moveTo(x, y - ry);
          context.bezierCurveTo(x + rx * .64, y - ry, x + rx, y - ry * .25, x + rx, y + ry * .19);
          context.bezierCurveTo(x + rx, y + ry * 1.24, x - rx, y + ry * 1.24, x - rx, y + ry * .19);
          context.bezierCurveTo(x - rx, y - ry * .25, x - rx * .64, y - ry, x, y - ry);
          context.closePath();
        };
        context.save();
        shape();
        context.clip();
        // A small magnified sample of the actual photograph gives each bead a transparent lens.
        const sx = image.naturalWidth / width, sy = image.naturalHeight / height;
        const sw = rx * 2 * sx / 1.48, sh = ry * 2 * sy / 1.48;
        const sourceX = Math.max(0, Math.min(image.naturalWidth - sw, x * sx - sw / 2));
        const sourceY = Math.max(0, Math.min(image.naturalHeight - sh, y * sy - sh / 2));
        context.globalAlpha = .92;
        context.drawImage(image, sourceX, sourceY, sw, sh, x - rx, y - ry, rx * 2, ry * 2);
        context.globalAlpha = 1;
        const shade = context.createLinearGradient(x - rx, y - ry, x + rx, y + ry);
        shade.addColorStop(0, 'rgba(235,249,255,.23)');
        shade.addColorStop(.28, 'rgba(255,255,255,0)');
        shade.addColorStop(.75, 'rgba(13,35,47,.06)');
        shade.addColorStop(1, 'rgba(13,35,47,.30)');
        context.fillStyle = shade;
        context.fillRect(x - rx, y - ry, rx * 2, ry * 2);
        const highlight = context.createRadialGradient(x - rx * .33, y - ry * .48, 0, x - rx * .33, y - ry * .48, r * .5);
        highlight.addColorStop(0, 'rgba(255,255,255,.62)');
        highlight.addColorStop(.35, 'rgba(255,255,255,.19)');
        highlight.addColorStop(1, 'rgba(255,255,255,0)');
        context.fillStyle = highlight;
        context.fillRect(x - rx, y - ry, rx * 2, ry * 2);
        const caustic = context.createRadialGradient(x + rx * .1, y + ry * .63, 0, x + rx * .1, y + ry * .63, r * .55);
        caustic.addColorStop(0, 'rgba(240,253,255,.32)');
        caustic.addColorStop(1, 'rgba(255,255,255,0)');
        context.fillStyle = caustic;
        context.fillRect(x - rx, y - ry, rx * 2, ry * 2);
        context.restore();
        context.lineWidth = .55;
        context.strokeStyle = 'rgba(15,35,48,.18)';
        shape();
        context.stroke();
        context.fillStyle = 'rgba(255,255,255,.52)';
        context.beginPath();
        context.ellipse(x - rx * .34, y - ry * .53, Math.max(.45, rx * .13), Math.max(.3, ry * .055), -.5, 0, Math.PI * 2);
        context.fill();
      };
      const draw = now => {
        frame = 0;
        if (!active || this.#reduced.matches || document.hidden) return;
        if (lastTime && now - lastTime < 32) { frame = requestAnimationFrame(draw); return; }
        const dt = Math.min((now - (lastTime || now)) / 1000, .05);
        lastTime = now;
        context.clearRect(0, 0, width, height);
        if (image.complete && image.naturalWidth && width && height) {
          for (const drop of drops) {
            if (drop.hidden > 0) { drop.hidden -= dt; continue; }
            if (drop.moving) {
              drop.phase += dt * 1.7;
              const surface = onVehicle(drop.x, drop.y);
              const grip = surface && Math.sin(drop.phase * .9) > .86 ? 0 : 1;
              if (surface) drop.speed = Math.min(drop.speed, 44 * scale);
              drop.y += drop.speed * dt * grip;
              drop.x += Math.sin(drop.phase) * dt * .7 * scale * grip;
              drop.speed = Math.min(drop.speed + dt * (surface ? 1.8 : 150) * scale, (surface ? 55 : 120) * scale);
              for (const bead of drops) {
                if (!surface || bead.moving || bead.hidden > 0) continue;
                if (Math.hypot(drop.x - bead.x, drop.y - bead.y) < drop.radius + bead.radius) {
                  drop.radius = Math.min(9.5 * scale, Math.sqrt(drop.radius ** 2 + bead.radius ** 2));
                  drop.speed = Math.min(55 * scale, drop.speed + 7 * scale);
                  bead.hidden = 5 + bead.phase % 4;
                }
              }
              if (drop.y > height + 12) {
                drop.y = -12;
                drop.x = width * (.08 + ((drop.x / width + .257) % .84));
                drop.radius = (5.8 + drop.phase % 2.3) * scale;
                drop.speed = (25 + drop.phase % 22) * scale;
              }
            }
            if (drop.y > -8) drawDrop(drop);
          }
        }
        frame = requestAnimationFrame(draw);
      };
      const stop = () => { cancelAnimationFrame(frame); frame = 0; lastTime = 0; context.clearRect(0, 0, width, height); };
      const resume = () => {
        stop();
        if (active && !this.#reduced.matches && !document.hidden) frame = requestAnimationFrame(draw);
      };
      const resize = new ResizeObserver(() => { measure(); resume(); });
      resize.observe(canvas);
      image.addEventListener('load', () => { measure(); resume(); }, { signal });
      document.addEventListener('visibilitychange', resume, { signal });
      this.#reduced.addEventListener('change', resume, { signal });
      measure();
      return {
        setActive(value) { if (active === value) return; active = value; resume(); },
        destroy() { stop(); resize.disconnect(); }
      };
    }
    disconnectedCallback() {
      this.#controller?.abort();
      this.#observer?.disconnect();
      this.#effectsObserver?.disconnect();
      for (const water of this.#water.values()) water.destroy();
      this.#water.clear();
      this.#resize?.disconnect();
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }
  }
  customElements.define('respecto-nv900-features', Nv900Features);
})();
